import React from "react";
import { Toggle } from "@velaro/velaro-component-lib";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TeamRoutingTier {
  tierNumber: number;
  ringTimeSeconds: number;
  alertPreviousTiers: boolean;
}

export interface UserTierAssignment {
  userId: number;
  tierNumber: number;
}

interface Props {
  userIds: number[];
  /** User display names, keyed by userId */
  userNames?: Record<number, string>;
  routingTiers: TeamRoutingTier[];
  userTierAssignments: UserTierAssignment[];
  onChange(tiers: TeamRoutingTier[], assignments: UserTierAssignment[]): void;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const MAX_TIERS = 3;
const TIER_COLORS: Record<number, string> = {
  1: "bg-blue-100 text-blue-800 border-blue-200",
  2: "bg-amber-100 text-amber-800 border-amber-200",
  3: "bg-purple-100 text-purple-800 border-purple-200"
};
const TIER_LABELS: Record<number, string> = {
  1: "Tier 1 — First Responders",
  2: "Tier 2 — Escalation",
  3: "Tier 3 — Final Escalation"
};

function defaultTier(tierNumber: number): TeamRoutingTier {
  return { tierNumber, ringTimeSeconds: 30, alertPreviousTiers: false };
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function TeamTierPanel({
  userIds,
  userNames = {},
  routingTiers,
  userTierAssignments,
  onChange
}: Props) {
  const enabled = routingTiers.length > 0;

  function handleToggle() {
    if (enabled) {
      // Disable — clear all tiers and assignments
      onChange([], []);
    } else {
      // Enable — default to Tier 1 only, all users in Tier 1
      onChange([defaultTier(1)], []);
    }
  }

  function updateTier(tierNumber: number, updates: Partial<TeamRoutingTier>) {
    const updated = routingTiers.map((t) =>
      t.tierNumber === tierNumber ? { ...t, ...updates } : t
    );
    onChange(updated, userTierAssignments);
  }

  function addTier() {
    if (routingTiers.length >= MAX_TIERS) return;
    const nextTierNumber = routingTiers.length + 1;
    onChange([...routingTiers, defaultTier(nextTierNumber)], userTierAssignments);
  }

  function removeTier(tierNumber: number) {
    const remaining = routingTiers.filter((t) => t.tierNumber !== tierNumber);
    // Renumber remaining tiers
    const renumbered = remaining.map((t, i) => ({ ...t, tierNumber: i + 1 }));
    // Remove assignments for the removed tier and renumber others
    const updatedAssignments = userTierAssignments
      .filter((a) => a.tierNumber !== tierNumber)
      .map((a) => ({
        ...a,
        tierNumber: a.tierNumber > tierNumber ? a.tierNumber - 1 : a.tierNumber
      }));
    onChange(renumbered, updatedAssignments);
  }

  function getUserTier(userId: number): number {
    return userTierAssignments.find((a) => a.userId === userId)?.tierNumber ?? 1;
  }

  function setUserTier(userId: number, tierNumber: number) {
    const filtered = userTierAssignments.filter((a) => a.userId !== userId);
    // Only store explicitly if not Tier 1 (null = Tier 1 on server)
    const updated =
      tierNumber === 1 ? filtered : [...filtered, { userId, tierNumber }];
    onChange(routingTiers, updated);
  }

  const userName = (id: number) => userNames[id] ?? `User #${id}`;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="w-full mt-2 mb-6">
      {/* Toggle header */}
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <label className="block text-sm font-bold text-black">
            Tier Escalation
          </label>
          <p className="text-xs text-slate-500 mt-0.5">
            Ring Tier 1 agents first. If no one accepts, escalate to Tier 2, then Tier 3.
          </p>
        </div>
        <Toggle toggled={enabled} changeToggle={handleToggle} />
      </div>

      {enabled && (
        <div className="mt-4 space-y-4">
          {/* Tier rows */}
          {routingTiers.map((tier) => (
            <TierRow
              key={tier.tierNumber}
              tier={tier}
              canRemove={tier.tierNumber === routingTiers.length && tier.tierNumber > 1}
              onUpdate={(updates) => updateTier(tier.tierNumber, updates)}
              onRemove={() => removeTier(tier.tierNumber)}
            />
          ))}

          {/* Add tier button */}
          {routingTiers.length < MAX_TIERS && (
            <button
              type="button"
              className="text-sm text-cornflower-blue-700 hover:text-cornflower-blue-900 hover:underline flex items-center gap-1 mt-1"
              onClick={addTier}
            >
              <span className="text-base leading-none">+</span>
              Add Tier {routingTiers.length + 1}
            </button>
          )}

          {/* Agent tier assignments */}
          {userIds.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-slate-700 mb-2">
                Assign agents to tiers
              </p>
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                {userIds.map((userId) => {
                  const currentTier = getUserTier(userId);
                  return (
                    <div
                      key={userId}
                      className="flex items-center justify-between px-3 py-2 bg-white hover:bg-slate-50"
                    >
                      <span className="text-sm text-slate-700">{userName(userId)}</span>
                      <div className="flex gap-1">
                        {routingTiers.map((t) => (
                          <button
                            key={t.tierNumber}
                            type="button"
                            onClick={() => setUserTier(userId, t.tierNumber)}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                              currentTier === t.tierNumber
                                ? TIER_COLORS[t.tierNumber]
                                : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            Tier {t.tierNumber}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Agents not assigned to a tier default to Tier 1.
              </p>
            </div>
          )}

          {/* Flow preview */}
          <FlowPreview tiers={routingTiers} />
        </div>
      )}
    </div>
  );
}

// ── TierRow ───────────────────────────────────────────────────────────────────

function TierRow({
  tier,
  canRemove,
  onUpdate,
  onRemove
}: {
  tier: TeamRoutingTier;
  canRemove: boolean;
  onUpdate(updates: Partial<TeamRoutingTier>): void;
  onRemove(): void;
}) {
  const colorClass = TIER_COLORS[tier.tierNumber] ?? "bg-slate-100 text-slate-700 border-slate-200";
  const label = TIER_LABELS[tier.tierNumber] ?? `Tier ${tier.tierNumber}`;

  return (
    <div className={`border rounded-lg p-3 ${colorClass.split(" ").slice(-1)[0]} bg-white`}>
      <div className="flex items-center gap-3">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${colorClass}`}
        >
          {label}
        </span>

        <div className="flex items-center gap-2 ml-auto">
          <label className="text-xs text-slate-500 whitespace-nowrap">Ring for</label>
          <input
            type="number"
            min={5}
            max={600}
            step={5}
            className="w-16 px-2 py-1 border border-slate-200 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-cornflower-blue-300"
            value={tier.ringTimeSeconds}
            onChange={(e) =>
              onUpdate({ ringTimeSeconds: Math.max(5, Number(e.target.value)) })
            }
          />
          <span className="text-xs text-slate-500">seconds</span>
        </div>

        {canRemove && (
          <button
            type="button"
            className="text-slate-400 hover:text-red-500 transition-colors ml-1"
            onClick={onRemove}
            title="Remove this tier"
          >
            ✕
          </button>
        )}
      </div>

      {tier.tierNumber > 1 && (
        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
          <input
            type="checkbox"
            id={`alertPrev-${tier.tierNumber}`}
            className="rounded"
            checked={tier.alertPreviousTiers}
            onChange={(e) => onUpdate({ alertPreviousTiers: e.target.checked })}
          />
          <label
            htmlFor={`alertPrev-${tier.tierNumber}`}
            className="text-xs text-slate-600 cursor-pointer"
          >
            Keep alerting previous tier(s) while ringing Tier {tier.tierNumber}
          </label>
        </div>
      )}
    </div>
  );
}

// ── Flow Preview ──────────────────────────────────────────────────────────────

function FlowPreview({ tiers }: { tiers: TeamRoutingTier[] }) {
  if (tiers.length < 2) return null;

  return (
    <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500 border border-slate-100">
      <p className="font-medium text-slate-600 mb-2">Escalation flow</p>
      <div className="flex flex-wrap items-center gap-1">
        {tiers.map((tier, i) => (
          <React.Fragment key={tier.tierNumber}>
            <span
              className={`px-2 py-0.5 rounded-full border font-medium ${TIER_COLORS[tier.tierNumber]}`}
            >
              Tier {tier.tierNumber}
            </span>
            <span className="text-slate-400">
              {tier.ringTimeSeconds}s
            </span>
            {i < tiers.length - 1 && (
              <span className="text-slate-300 mx-1">→</span>
            )}
          </React.Fragment>
        ))}
        <span className="text-slate-300 mx-1">→</span>
        <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-500 font-medium">
          No answer
        </span>
      </div>
    </div>
  );
}
